import { useFrame } from '@react-three/fiber'
import { useMemo, useRef } from 'react'
import * as THREE from 'three'
import { useAppStore } from '../store'

interface Props {
  bones: Map<string, THREE.Bone>
}

export function LayerOverlays({ bones }: Props) {
  const layers = useAppStore((s) => s.layers)
  const skeletonGroup = useRef<THREE.Group>(null)
  const organGroup = useRef<THREE.Group>(null)

  const bonePairs = useMemo(() => {
    const pairs: { a: string; b: string }[] = []
    for (const [name, bone] of bones) {
      for (const child of bone.children) {
        if ((child as THREE.Bone).isBone) pairs.push({ a: name, b: child.name })
      }
    }
    return pairs
  }, [bones])

  useFrame(() => {
    if (layers.skeleton && skeletonGroup.current) {
      skeletonGroup.current.children.forEach((child, i) => {
        const pair = bonePairs[i]
        if (!pair || !(child instanceof THREE.Mesh)) return
        const a = bones.get(pair.a)
        const b = bones.get(pair.b)
        if (!a || !b) return
        const start = new THREE.Vector3()
        const end = new THREE.Vector3()
        a.getWorldPosition(start)
        b.getWorldPosition(end)
        const mid = start.clone().add(end).multiplyScalar(0.5)
        const dir = end.clone().sub(start)
        const len = dir.length()
        if (len < 0.001) return
        child.position.copy(mid)
        child.scale.set(1, len, 1)
        child.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize())
      })
    }
    if (organGroup.current) {
      organGroup.current.children.forEach((child) => {
        const mesh = child as THREE.Mesh & { userData: { bone?: string; offset?: THREE.Vector3 } }
        if (!mesh.userData.bone || !mesh.userData.offset) return
        const bone = bones.get(mesh.userData.bone)
        if (!bone) return
        bone.updateWorldMatrix(true, false)
        mesh.position.copy(mesh.userData.offset.clone().applyMatrix4(bone.matrixWorld))
        mesh.quaternion.setFromRotationMatrix(bone.matrixWorld)
      })
    }
  })

  return (
    <group>
      {layers.skeleton && (
        <group ref={skeletonGroup}>
          {bonePairs.map((p) => (
            <mesh key={`${p.a}-${p.b}`}>
              <cylinderGeometry args={[0.01, 0.01, 1, 6]} />
              <meshStandardMaterial color="#f5f0e6" transparent opacity={0.8} />
            </mesh>
          ))}
        </group>
      )}
      <group ref={organGroup}>
        {layers.spine &&
          ['mixamorigSpine', 'mixamorigSpine1', 'mixamorigSpine2', 'mixamorigNeck'].map((n) => (
            <mesh key={`sp-${n}`} userData={{ bone: n, offset: new THREE.Vector3(0, 0, -0.04) }} scale={[0.05, 0.035, 0.05]}>
              <boxGeometry />
              <meshStandardMaterial color="#7ec8c8" transparent opacity={0.7} depthWrite={false} />
            </mesh>
          ))}
        {layers.organs && (
          <mesh userData={{ bone: 'mixamorigSpine', offset: new THREE.Vector3(0, 0.06, 0.06) }} scale={[0.11, 0.13, 0.07]}>
            <sphereGeometry args={[1, 12, 10]} />
            <meshStandardMaterial color="#c45c6a" transparent opacity={0.4} depthWrite={false} />
          </mesh>
        )}
        {layers.circulation && (
          <mesh userData={{ bone: 'mixamorigSpine2', offset: new THREE.Vector3(0, 0.02, 0.05) }} scale={[0.07, 0.06, 0.05]}>
            <sphereGeometry args={[1, 12, 10]} />
            <meshStandardMaterial color="#e63946" transparent opacity={0.4} depthWrite={false} />
          </mesh>
        )}
        {layers.respiratory && (
          <>
            <mesh userData={{ bone: 'mixamorigSpine2', offset: new THREE.Vector3(-0.07, 0.04, 0.05) }} scale={[0.06, 0.09, 0.045]}>
              <sphereGeometry args={[1, 12, 10]} />
              <meshStandardMaterial color="#7eb6d9" transparent opacity={0.4} depthWrite={false} />
            </mesh>
            <mesh userData={{ bone: 'mixamorigSpine2', offset: new THREE.Vector3(0.07, 0.04, 0.05) }} scale={[0.06, 0.09, 0.045]}>
              <sphereGeometry args={[1, 12, 10]} />
              <meshStandardMaterial color="#7eb6d9" transparent opacity={0.4} depthWrite={false} />
            </mesh>
          </>
        )}
        {layers.pelvic && (
          <mesh userData={{ bone: 'mixamorigHips', offset: new THREE.Vector3(0, -0.02, 0.02) }} scale={[0.09, 0.05, 0.07]}>
            <sphereGeometry args={[1, 12, 10]} />
            <meshStandardMaterial color="#d4a5c9" transparent opacity={0.4} depthWrite={false} />
          </mesh>
        )}
      </group>
    </group>
  )
}
